import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../components/Modal";
import { updateIndividualLedgerAction } from "../../../../actions/updateIndividualLedgerAction";
import { getUserData } from "../../../../utils/authnicate";
import "./index.scss";

/**
 * AddNewLedgerUpload
 * Opens when "Add New Ledger" is clicked from the action menu.
 * Lets the user enter a ledger name and saves it via update-individual-ledger API.
 */
const AddNewLedgerUpload = ({ isOpen, onClose, entityRow, onSuccess }) => {
  const [ledgerName, setLedgerName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setLedgerName("");
      setError("");
    }
  }, [isOpen, entityRow]);

  const entity = entityRow?.entity || {};
  const entityId = entity.entityId || "";
  const entityName = entity.entityName || entity.EntityName || "—";
  const entityPrimaryId = entity.entityPrimaryId || entity.id || 0;

  const handleClose = () => {
    setLedgerName("");
    setError("");
    onClose();
  };

  const handleSave = async () => {
    const trimmed = ledgerName.trim();
    if (!trimmed) {
      setError("Ledger name is required");
      return;
    }

    setSubmitting(true);
    setError("");

    const loggedInUser = getUserData();
    const createdBy = loggedInUser?.email || loggedInUser?.name || "Admin";

    const result = await updateIndividualLedgerAction({
      entityPrimaryId,
      entityId,
      ledgerName: trimmed,
      entityName,
      createdBy,
    });

    setSubmitting(false);

    if (result.success) {
      toast.success(result.message || "Ledger added successfully");
      if (onSuccess) onSuccess();
      handleClose();
    } else {
      // Show error inline on the field (e.g. "Ledger Name Already Exists.")
      setError(result.message || "Failed to add ledger");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add New Ledger"
      size="small"
    >
      <div className="anl-body">
        {/* Entity info */}
        <div className="anl-entity-info">
          <span className="anl-entity-info__id">{entityId}</span>
          <span className="anl-entity-info__name">{entityName}</span>
        </div>

        {/* Ledger name input */}
        <div className="anl-field">
          <label className="anl-field__label" htmlFor="anl-ledger-name">
            Ledger Name <span className="anl-field__required">*</span>
          </label>
          <input
            id="anl-ledger-name"
            type="text"
            className={`anl-field__input${error ? " anl-field__input--error" : ""}`}
            placeholder="Enter ledger name"
            value={ledgerName}
            onChange={(e) => {
              setLedgerName(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
            autoFocus
            disabled={submitting}
          />
          {error && <span className="anl-field__error">{error}</span>}
        </div>

        {/* Actions */}
        <div className="anl-footer">
          <button
            className="anl-btn anl-btn--cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="anl-btn anl-btn--save"
            onClick={handleSave}
            disabled={submitting || !ledgerName.trim()}
          >
            {submitting ? (
              <>
                <span className="anl-btn__spinner" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddNewLedgerUpload;
